import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Icon, BtnIcon } from '@growthaxl/web-foundation';

import type { EsaPanelMode } from '../../../../shell/store/shellStore';

export interface ESATopBarProps {
  threadTitle: string | null;
  panelMode: EsaPanelMode;
  hitlCount?: number;
  /** When true shows the "SAVED" badge next to the conversation title. */
  savedStatus?: boolean;
  /** When true shows a pulsing red dot on the My Inbox button. Cleared on inbox open. */
  hasUnreadNotification?: boolean;
  onClose: () => void;
  /** Canonical callback for the "My Inbox" button. */
  onInbox?: () => void;
  /** @deprecated Use onInbox instead. Kept for backward compatibility. */
  onSettings?: () => void;
  onNewConversation?: () => void;
  onSwitchConversation?: () => void;
  /** Called with the committed new title when the user finishes renaming. */
  onRenameCommit?: (title: string) => void;
  /** Download a readable .txt transcript of the active conversation. */
  onDownloadTranscript?: () => void;
  /** When true, disables the download menu item (e.g. no messages yet). */
  downloadDisabled?: boolean;
  /** Soft-delete / archive the active conversation. */
  onDeleteConversation?: () => void;
  className?: string;
}

/* ── Inline SVG icons (not in foundation registry) ── */
const ICN_SWITCH_CONVERSATION = (
  <Icon name="switch_conversation" className="!size-6 shrink-0 text-text-secondary" />
);
const ICN_ARROW_DOWN = (
  <Icon name="arrow_down" className="!size-5 shrink-0 text-text-primary" />
);
const ICN_ADD = (
  <Icon name="add" className="!size-7 shrink-0 text-text-secondary" />
);
const ICN_EDIT = (
  <Icon name="edit" className="!size-6 shrink-0 text-text-secondary" />
);
const ICN_DOWNLOAD = (
  <BtnIcon name="download" size={16} className="shrink-0 text-text-secondary" />
);
const ICN_TRASH = (
  <BtnIcon name="trash" size={16} className="shrink-0 text-error" />
);

/** Matches UserMenu dropdown item spacing and typography. */
const ESA_MENU_ITEM_CLASS =
  'flex w-full items-center gap-3 border-0 bg-transparent px-5 py-2.5 text-left font-inherit cursor-pointer transition-colors duration-fast hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';
const ESA_MENU_ITEM_TEXT_CLASS = 'text-label leading-normal font-normal';

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}
function MenuItem({ icon, label, onClick, disabled = false, danger = false }: MenuItemProps): React.ReactElement {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={ESA_MENU_ITEM_CLASS}
    >
      <span className="flex w-6 shrink-0 items-center justify-center">{icon}</span>
      <Typography
        variant="body2"
        color="primary"
        component="span"
        className={`${ESA_MENU_ITEM_TEXT_CLASS}${danger ? ' !text-error' : ''}`}
      >
        {label}
      </Typography>
    </button>
  );
}

/** Shared label style for My Inbox and New conversation. */
const ESA_NAV_LABEL_CLASS = 'text-body-md font-normal text-text-primary';

/** Ghost icon-text button used for My Inbox — matches the reference design's lighter style. */
function GhostIconTextButton({
  icon,
  label,
  onClick,
  ariaLabel,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  ariaLabel?: string;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 min-h-8 py-1 px-3 rounded-6 border border-border-default bg-surface text-text-secondary hover:bg-surface-hover cursor-pointer transition-colors shrink-0"
      aria-label={ariaLabel ?? label}
    >
      <span className="flex w-6 shrink-0 items-center justify-center">{icon}</span>
      <span className={ESA_NAV_LABEL_CLASS}>{label}</span>
    </button>
  );
}

export function ESATopBar({
  threadTitle,
  panelMode: _panelMode,
  hitlCount = 0,
  savedStatus = false,
  hasUnreadNotification = false,
  onClose,
  onInbox,
  onSettings,
  onNewConversation,
  onSwitchConversation,
  onRenameCommit,
  onDownloadTranscript,
  downloadDisabled = false,
  onDeleteConversation,
  className,
}: ESATopBarProps): React.ReactElement {
  const handleInboxClick = onInbox ?? onSettings ?? (() => undefined);
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // ── Inline rename state ───────────────────────────────────────────────────
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Kept in sync with renameValue so commitRename never captures a stale closure.
  const renameValueRef = useRef('');

  const startRename = (): void => {
    const initial = threadTitle ?? t('esa.newConversation', 'New conversation');
    renameValueRef.current = initial;
    setRenameValue(initial);
    setIsRenaming(true);
  };

  const commitRename = (): void => {
    // Read directly from the DOM element to avoid any stale state/ref issues.
    const trimmed = (renameInputRef.current?.value ?? renameValueRef.current).trim();
    if (trimmed) onRenameCommit?.(trimmed);
    setIsRenaming(false);
  };

  const cancelRename = (): void => {
    setIsRenaming(false);
  };

  // Auto-focus + select-all when rename starts
  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!menuOpen) return;
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  const displayName = threadTitle ?? t('esa.newConversation', 'New conversation');
  const showSecondaryActions = Boolean(
    onRenameCommit || onDownloadTranscript || onDeleteConversation,
  );

  return (
    <div
      role="banner"
      className={`flex items-center gap-2 shrink-0 relative bg-surface border-b border-border-default px-2 py-2 min-h-[48px] ${className ?? ''}`}
    >
      {/* Left: ESA identity avatar + conversation dropdown trigger / rename input */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Dropdown click-outside detection wraps only the trigger + menu */}
        <div className="flex items-center gap-1 flex-1 min-w-0" ref={menuRef}>

        {/* Conversation dropdown trigger OR inline rename input */}
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={e => { renameValueRef.current = e.target.value; setRenameValue(e.target.value); }}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
            }}
            aria-label={t('esa.renameConversation', 'Rename conversation')}
            className="flex-1 min-w-0 h-7 px-2 text-sm font-semibold text-text-primary bg-surface border border-border-focus rounded-6 outline-none"
          />
        ) : (
          <button
            onClick={() => setMenuOpen(o => !o)}
            className={`flex items-center gap-1.5 flex-1 min-w-0 h-7 px-2 rounded-6 border-none cursor-pointer hover:bg-surface-muted transition-colors ${menuOpen ? 'bg-surface-muted' : 'bg-transparent'}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={displayName}
          >
            <span className="flex w-6 shrink-0 items-center justify-center text-text-secondary">{ICN_SWITCH_CONVERSATION}</span>
            <span className={`truncate min-w-0 ${ESA_NAV_LABEL_CLASS}`}>
              {displayName}
            </span>
            {savedStatus && (
              <Typography variant="overline" component="span" className="font-bold text-success-text shrink-0">
                SAVED
              </Typography>
            )}
            <span className="text-text-primary flex shrink-0">{ICN_ARROW_DOWN}</span>
          </button>
        )}

        {/* Dropdown menu */}
        {menuOpen && (
          <div
            role="menu"
            className="absolute left-0 top-full z-50 mt-1 w-[260px] overflow-hidden rounded-12 border border-border-default bg-surface-elevated text-text-primary shadow-lg"
          >
            <div className={showSecondaryActions ? 'border-b border-border-default py-1.5' : 'py-1.5'}>
              <MenuItem
                icon={ICN_ADD}
                label={t('esa.newConversation', 'New conversation')}
                onClick={() => { setMenuOpen(false); onNewConversation?.(); }}
              />
              <MenuItem
                icon={ICN_SWITCH_CONVERSATION}
                label={t('esa.switchConversation', 'Switch conversation')}
                onClick={() => { setMenuOpen(false); onSwitchConversation?.(); }}
              />
            </div>
            {showSecondaryActions && (
              <div className="py-1.5">
                {onDownloadTranscript && (
                  <MenuItem
                    icon={ICN_DOWNLOAD}
                    label={t('esa.downloadTranscript', 'Download transcript')}
                    disabled={downloadDisabled}
                    onClick={() => {
                      if (downloadDisabled) return;
                      setMenuOpen(false);
                      onDownloadTranscript();
                    }}
                  />
                )}
                {onRenameCommit && (
                  <MenuItem
                    icon={ICN_EDIT}
                    label={t('esa.renameConversation', 'Rename')}
                    onClick={() => { setMenuOpen(false); startRename(); }}
                  />
                )}
                {onDeleteConversation && (
                  <MenuItem
                    icon={ICN_TRASH}
                    label={t('esa.deleteConversation', 'Delete conversation')}
                    danger
                    onClick={() => { setMenuOpen(false); onDeleteConversation(); }}
                  />
                )}
              </div>
            )}
          </div>
        )}
        </div> {/* end menuRef wrapper */}
      </div> {/* end left section */}

      {/* Right: My Inbox ghost button + collapse panel button */}
      <div className="flex items-center gap-3 shrink-0">
        <GhostIconTextButton
          icon={
            <Icon
              name={hasUnreadNotification ? 'direct_notification_active' : 'direct_notification'}
              className={`!size-7 shrink-0${hasUnreadNotification ? ' animate-pulse' : ''}`}
            />
          }
          label={t('esa.myInbox', 'My Inbox')}
          onClick={handleInboxClick}
          ariaLabel={
            hitlCount > 0
              ? `${t('esa.myInbox', 'My Inbox')} (${hitlCount} pending)`
              : undefined
          }
        />

        {/* Collapse panel — foundation sidebar-left icon */}
        <button
          onClick={onClose}
          className="flex items-center justify-center size-8 rounded-6 border-none bg-transparent cursor-pointer text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
          aria-label={t('esa.closePanel', 'Collapse panel')}
        >
          <Icon name="sidebar_left" className="!size-6 shrink-0" />
        </button>
      </div>
    </div>
  );
}
